import { useMemo } from 'react'
import { IconPackage } from '@tabler/icons-react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { Link } from 'react-router-dom'
import { Anchor, Group, Badge, Stack, Text } from '@mantine/core'
import { PillAlert } from '@shared/components/pill-alert/pill-alert'
import { tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { Value } from '@/components/shared/value/value'
import { WoolLotDataProps } from '@/components/typing/program/typing-program-types'
import { formatDate } from '@shared/helpers/format-date/format-date'
import { WoolSoldStatus } from '@loom-api-types'
import { AlertLevel, IconAlert } from '@shared/components/icon-alert/icon-alert'

export interface PreviousLotsTableProps {
  data?: Array<WoolLotDataProps>
  renderKey?: string
  noDataMessage?: string
  isLoading?: boolean
  isError?: boolean
}

export const PreviousLotsTable = ({
  data = [],
  renderKey = '',
  noDataMessage = 'No previous lots found within the last 18 months',
  isLoading,
  isError = false,
}: PreviousLotsTableProps) => {
  const columns = useMemo<MRT_ColumnDef<WoolLotDataProps>[]>(
    () => [
      {
        id: 'woolLotCommissionPaymentDate',
        accessorKey: 'woolLotCommissionPaymentDate',
        header: 'Lotted',
        sortingFn: 'datetime',
        Cell: ({ cell }) => formatDate(cell.getValue<Date>(), 'DD/MM/YY'),
      },
      {
        id: 'woolLotReferenceNumber',
        accessorKey: 'woolLotReferenceNumber',
        header: 'Ref No',
        Cell: ({ row }) => (
          <Stack align='flex-start'>
            <Anchor
              component={Link}
              to={`/typing/program?reference=${row.original.woolLotReferenceNumber}`}
              size='sm'
            >
              {row.original.woolLotReferenceNumber}
            </Anchor>
            {row.original.coreTestBoxLocation && (
              <Badge
                color='gray'
                fw={600}
                aria-label={`Box location ${row.original.coreTestBoxLocation}`}
              >
                <Group wrap='nowrap' gap='xxs'>
                  <IconPackage size={14} stroke={2} />
                  {row.original.coreTestBoxLocation}
                </Group>
              </Badge>
            )}
          </Stack>
        ),
      },
      {
        id: 'baleHeaderPurchaseDescription',
        accessorKey: 'baleHeaderPurchaseDescription',
        header: 'Desc/Type',
        Cell: ({ row }) => (
          <>
            <div>{row.original.baleHeaderPurchaseDescription || '-'}</div>
            <div>{row.original.woolLotTypeCurrent || '-'}</div>
            <div>{row.original.woolLotInternalComment || '-'}</div>
          </>
        ),
      },
      {
        id: 'purchaseAccountName',
        accessorKey: 'purchaseAccountName',
        header: 'Purchase Account',
        Cell: ({ row }) => (
          <Stack align='flex-start'>
            <div>{row.original.purchaseAccountName}</div>
            {row.original.woolLotSoldStatus === WoolSoldStatus.SoldOrBin && (
              <Badge color='gray' variant='light' fw={600} aria-label='Sold'>
                Sold
              </Badge>
            )}
          </Stack>
        ),
      },
      {
        id: 'coreTestMicron',
        accessorKey: 'coreTestMicron',
        header: 'Micron',
        Cell: ({ row }) => (
          <>
            <Value value={row.original.coreTestMicronLaserscan} unit='LS' size='sm' />
            <Value value={row.original.coreTestMicronOfda} unit='OF' size='sm' />
            <Value value={row.original.coreTestMicronAirflow} unit='AF' size='sm' />
          </>
        ),
      },
      {
        id: 'coreTestColourYZ',
        accessorKey: 'coreTestColourYZ',
        header: 'Y-Z',
      },
      {
        id: 'coreTestCoefficientOfVariationLaserscan',
        accessorKey: 'coreTestCoefficientOfVariationLaserscan',
        header: 'Mic CV',
        Cell: ({ row }) => (
          <>
            <Value
              value={row.original.coreTestCoefficientOfVariationLaserscan}
              unit='CV'
              size='sm'
            />
            <Value value={row.original.coreTestCurvatureMeanLaser} unit='CUR' size='sm' />
          </>
        ),
      },
      {
        id: 'coreTestVegetableMatterBase',
        accessorKey: 'coreTestVegetableMatterBase',
        header: 'Yield',
        Cell: ({ row }) => (
          <>
            <Value value={row.original.coreTestVegetableMatterBase} unit='VM' size='sm' />
            <Value value={row.original.coreTestYield1} size='sm' />
          </>
        ),
      },
      {
        id: 'coreTestStapleStrength',
        accessorKey: 'coreTestStapleStrength',
        header: 'SS',
        Cell: ({ row }) => (
          <>
            <Value value={row.original.coreTestStapleStrength} unit='NKT' size='sm' />
            <Value value={row.original.coreTestStapleAverageStrength} unit='SS25' size='sm' />
          </>
        ),
      },
      {
        id: 'coreTestStapleLengthGreasy',
        accessorKey: 'coreTestStapleLengthGreasy',
        header: 'SL',
        Cell: ({ row }) => (
          <>
            <Value value={row.original.coreTestStapleLengthGreasy} unit='MM' size='sm' />
            <Value
              value={row.original.coreTestStapleCoefficientOfLengthVariation}
              unit='CV%'
              size='sm'
            />
          </>
        ),
      },
      {
        id: 'coreTestHauteurLength',
        accessorKey: 'coreTestHauteurLength',
        header: 'TH',
      },
      {
        id: 'coreTestPointOfBreakTip',
        accessorKey: 'coreTestPointOfBreakTip',
        header: 'POB',
        Cell: ({ row }) => (
          <>
            <Value
              value={row.original.coreTestPointOfBreakTip}
              unit='T'
              isUnitTrailing={false}
              size='sm'
            />
            <Value
              value={row.original.coreTestPointOfBreakMid}
              unit='M'
              isUnitTrailing={false}
              size='sm'
            />
            <Value
              value={row.original.coreTestPointOfBreakBase}
              unit='B'
              isUnitTrailing={false}
              size='sm'
            />
          </>
        ),
      },
      {
        id: 'woolLotLineNumber',
        accessorKey: 'woolLotLineNumber',
        header: 'Sale',
        Cell: ({ row }) => (
          <>
            <Value value={row.original.woolLotLineNumber} unit='LINE' size='sm' />
            <Value value={row.original.woolLotCode} unit='LOT' size='sm' />
            <Value value={row.original.woolLotSaleAndSeason} size='sm' />
          </>
        ),
      },
      {
        id: 'woolLotBales',
        accessorKey: 'woolLotBales',
        header: 'BLS',
      },
      {
        id: 'qualityScheme',
        accessorKey: 'qualityScheme',
        header: 'Cert',
        Cell: ({ row }) => (
          <>
            <Text size='sm' c={row.original.qualitySchemeWarning ? 'red.9' : ''}>
              {row.original.qualityScheme}
            </Text>
            {row.original.qualitySchemeWarning && <PillAlert text='Cert' />}
          </>
        ),
      },
      {
        id: 'woolLotsTotalKilosNet',
        accessorKey: 'woolLotsTotalKilosNet',
        header: 'Total',
        Cell: ({ row }) => (
          <>
            <Value value={row.original.woolLotsTotalKilosNet} unit='NET' size='sm' />
            <Value value={row.original.woolLotsTotalKilosClean} unit='CLN' size='sm' />
          </>
        ),
      },
    ],
    []
  )

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    state: {
      showSkeletons: isLoading,
    },
    enableSorting: true,
    initialState: {
      sorting: [
        {
          id: 'woolLotCommissionPaymentDate', // Default sort by date
          desc: true,
        },
      ],
    },
    enablePagination: false,
    renderEmptyRowsFallback: () =>
      isError ? (
        <IconAlert level={AlertLevel.ERROR} title='Error loading previous lots' />
      ) : (
        <NoDataMessage noDataMessage={noDataMessage} />
      ),
    mantineTableBodyRowProps: ({ row }) => {
      const isSold = row.original.woolLotSoldStatus === WoolSoldStatus.SoldOrBin
      return isSold ? { bg: 'gray.1', c: 'gray.5' } : {}
    },
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Previous Lots',
    },
  })

  return <MantineReactTable table={table} key={renderKey} />
}
